import { useEffect, useState } from "react";
import { toast } from "sonner";

import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import {
  getOppositionEvidenceConfirmation,
  getOppositionProgress,
  recordOppositionSignatureFromClientForEvidence,
  OppositionWorkflowStep,
  type OppositionEvidenceConfirmationItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_SIGNATURE_FOR_EVIDENCE_FORM_ID =
  "opposition-signature-for-evidence-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionSignatureFromClientForEvidenceView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [marks, setMarks] = useState<OppositionEvidenceConfirmationItem[]>([]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionEvidenceConfirmation(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        setMarks(
          data.conflictingMarks.filter((m) => m.proceedWithEvidence === true)
        );
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_12_SignatureFromClientForEvidenceByOpponent
          );
          setRecorded(Boolean(step?.isCompleted));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load step data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || recording) return;
    if (recorded) {
      onSuccess?.(leadId);
      return;
    }
    if (marks.length === 0) {
      toast.error(
        "No eligible applications. Mark proceedWithEvidence=YES in Step 5 first."
      );
      return;
    }
    setRecording(true);
    try {
      await recordOppositionSignatureFromClientForEvidence(leadId, {
        conflictingMarkIds: marks.map((m) => m.id),
      });
      setRecorded(true);
      toast.success("Signature step recorded.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record signature step"
      );
    } finally {
      setRecording(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_SIGNATURE_FOR_EVIDENCE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex w-full max-w-[1085px] flex-col items-center justify-center gap-3 py-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={legalAdviceHeadset}
        alt=""
        className="w-[120px] h-[120px] opacity-80"
      />
      <p
        className="text-base font-semibold text-[#222]"
        style={{ lineHeight: "24px" }}
      >
        Signature Process
      </p>
      <p
        className="max-w-[520px] text-center text-sm text-[#6D6D6D]"
        style={{ lineHeight: "20px" }}
      >
        Please ensure the Power of Attorney (Form 48) and User Affidavit (if
        applicable) are printed on the stamp papers procured in Step 5 and
        signed by the client.
      </p>
      {recorded && (
        <span className="text-xs font-semibold text-[#222]">
          Signature step already recorded.
        </span>
      )}
    </form>
  );
}
